import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Wallet, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getCashFlowSummary } from "@/lib/api-financial";
import { formatBRL } from "@/lib/utils";

export default function ProducerCashFlow({ producerId }: { producerId: string }) {
  const { t } = useTranslation("producerFinancial");
  const { data, isLoading } = useQuery({
    queryKey: ["cash-flow", producerId],
    queryFn: () => getCashFlowSummary(producerId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;
  }

  const summary = data || {
    totalReceivable: 0,
    totalPayable: 0,
    confirmedReceivable: 0,
    confirmedPayable: 0,
    pendingReceivable: 0,
    pendingPayable: 0,
    balance: 0,
  };

  const cards = [
    { title: t("cashFlow.cards.netBalance", "Saldo Líquido"), value: formatBRL(summary.balance), icon: Wallet, color: summary.balance >= 0 ? "text-primary" : "text-muted-foreground" },
    { title: t("cashFlow.cards.totalReceivable", "Total a Receber"), value: formatBRL(summary.totalReceivable), icon: TrendingUp, color: "text-primary", sub: t("cashFlow.cards.pending", { amount: formatBRL(summary.pendingReceivable), defaultValue: "{{amount}} pendente" }) },
    { title: t("cashFlow.cards.totalPayable", "Total a Pagar"), value: formatBRL(summary.totalPayable), icon: TrendingDown, color: "text-muted-foreground", sub: t("cashFlow.cards.pending", { amount: formatBRL(summary.pendingPayable), defaultValue: "{{amount}} pendente" }) },
    { title: t("cashFlow.cards.confirmed", "Confirmado"), value: formatBRL(summary.confirmedReceivable - summary.confirmedPayable), icon: Clock, color: "text-foreground" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        {cards.map((card) => (
          <Card key={card.title} className="border border-border/40 bg-card shadow-none rounded-2xl">
            <CardHeader className="pb-1 sm:pb-2 p-3.5 sm:p-5">
              <CardTitle className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <card.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{card.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3.5 pb-3.5 sm:px-5 sm:pb-5">
              <p className={`text-lg sm:text-2xl font-bold tabular-nums ${card.color}`}>{card.value}</p>
              {card.sub && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{card.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-border/40 bg-card shadow-none rounded-2xl">
        <CardHeader className="pb-2 p-4 sm:p-5 border-b border-border/30">
          <CardTitle className="text-sm sm:text-base font-semibold text-foreground">{t("cashFlow.summary.title", "Resumo do Fluxo de Caixa")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-5">
          <div className="space-y-2.5 text-xs sm:text-sm">
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">{t("cashFlow.summary.confirmedRevenue", "Receitas confirmadas")}</span>
              <span className="font-semibold text-primary tabular-nums">{formatBRL(summary.confirmedReceivable)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">{t("cashFlow.summary.pendingRevenue", "Receitas pendentes")}</span>
              <span className="font-medium text-muted-foreground tabular-nums">{formatBRL(summary.pendingReceivable)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">{t("cashFlow.summary.confirmedExpenses", "Despesas confirmadas")}</span>
              <span className="font-medium text-muted-foreground tabular-nums">-{formatBRL(summary.confirmedPayable)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">{t("cashFlow.summary.pendingExpenses", "Despesas pendentes")}</span>
              <span className="font-medium text-muted-foreground tabular-nums">-{formatBRL(summary.pendingPayable)}</span>
            </div>
            <div className="flex justify-between pt-2 font-semibold text-foreground">
              <span>{t("cashFlow.summary.projectedBalance", "Saldo projetado")}</span>
              <span className={`tabular-nums ${summary.balance >= 0 ? "text-primary font-bold" : "text-muted-foreground"}`}>{formatBRL(summary.balance)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
